import { useMemo, useState, useEffect, useContext } from 'react';

import { useStoreInformation } from "./stores/storeInformationStore";
import { useOrders } from '../model/useOrders';
import { usePurchaseRecommendation } from '../model/usePurchaseRecommendation';

import { TenantContext } from '../templates/context/TenantContext';

import { 
    ORDER_STATUS, 
    COLUMNS_IDENTIFIERS, 
    ORDER_ITEM_STATUS, 
    COLUMNS_LABELS, 
} from '../components/components.constants';
import { EffectivePurchaseCell, PackToUnitRatioCell, PrefixNameCell } from '../components/SharedComponents/sharedComponents.index';

import { isEligibleForPurchase } from './strategy/purchaseOrderEligibility/withOriginalValues/withOriginalValues';
import { createPurchaseValueStrategy } from "./strategy/effectivePurchaseValue/index";

/** View model utilizado para exibição e construção de payload quando os usuários
 * estão prestes a gerar uma ordem de pedido. Constam as regras de negócio e métodos
 * necessários para essa operação.
 * 
 */
export const useOrderDetailsModalVM = ({ actions, ctx, uiState }) => {

    const { onOrderComplete, setInputs } = actions || {};

    const { orderData, prefixes, purchaseRecommendationDataOnly = [] } = ctx || {};

    const { inputs, showInputs, saveLoading } = uiState || {};

    const { storeId } = useStoreInformation();
    const { userInfo, username } = useContext(TenantContext);

    const { createOrder, isCreatingOrder, isCreateOrderSuccess, createdOrder } = useOrders(storeId);
    const { refetch } = usePurchaseRecommendation(storeId);

    // funções para identificação de dados de fornecedores
    const extractSupplierIds = (orderData) => {
        if (orderData?.selectedSupplier) return [orderData.selectedSupplier.id];

        const idsSet = new Set();
        orderData?.filteredData?.forEach(item => {
            item.suppliers?.forEach(s => idsSet.add(s.supplierId));
        });

        return Array.from(idsSet);
    };

    const findInitialSupplierIndex = (selectedSupplierId, supplierIds) => {
        if (selectedSupplierId) {
            const index = supplierIds.indexOf(selectedSupplierId);
            return index >= 0 ? index : 0;
        }
        return 0;
    };

    const filterProductsBySupplier = (products, supplierId, hasMultipleSuppliers) => {
        if (!products?.length) return [];

        if (!hasMultipleSuppliers) {
            const productsWithSuppliers = products.filter(
                p => Array.isArray(p.suppliers) && p.suppliers.length > 0
            );
            return productsWithSuppliers.length === products.length ?
                products : productsWithSuppliers;
        }

        return products.filter(p => p.suppliers?.some(s => s.supplierId === supplierId));
    };

    const getSupplierName = (orderData, supplierIds, currentSupplierIndex) => {
        if (orderData?.selectedSupplier?.name) return orderData.selectedSupplier.name;

        if (!orderData?.filteredData || supplierIds.length === 0) return '';

        const supplierId = supplierIds[currentSupplierIndex];
        const supplier = orderData.filteredData
            ?.flatMap(p => p.suppliers ?? [])
            ?.find(s => s.supplierId === supplierId);

        return supplier?.supplierName ?? '';
    };

    const supplierIds = useMemo(() => extractSupplierIds(orderData), [orderData]);
    const hasMultipleSuppliers = supplierIds.length > 1;

    const [currentSupplierIndex, setCurrentSupplierIndex] = useState(() => findInitialSupplierIndex(orderData?.selectedSupplierId, supplierIds) );

    const currentSupplierId = supplierIds[currentSupplierIndex];

    const currentSupplierName = useMemo(
        () => getSupplierName(orderData, supplierIds, currentSupplierIndex),
        [orderData, supplierIds, currentSupplierIndex]
    );    

    // funções para identificação de produtos
    const filteredProducts = useMemo(() => {
        if (!orderData?.filteredData) return [];
        return filterProductsBySupplier(orderData.filteredData, currentSupplierId, hasMultipleSuppliers);
    }, [orderData?.filteredData, currentSupplierId, hasMultipleSuppliers]);

    const matchedRecommendations = useMemo(() => {
        return purchaseRecommendationDataOnly.filter(rec =>
            filteredProducts.some(prod => prod.purchaseRecommendationId === rec.purchaseRecommendationId)
        );
    }, [purchaseRecommendationDataOnly, filteredProducts]);

    const enrichedProducts = useMemo(() =>
        filteredProducts
            .map(product => {
            const match = matchedRecommendations.find(
                rec => rec.purchaseRecommendationId === product.purchaseRecommendationId
            );
            const enriched = match ? { ...product, ...match } : null;
            
            return enriched;
            })
            .filter(Boolean)
            .filter(isEligibleForPurchase),
        [filteredProducts, matchedRecommendations]
    );
    
    const [products, setProducts] = useState(enrichedProducts);
    const [processedSupplierIds, setProcessedSupplierIds] = useState([]);

    // funções para geração de dados de identificação da loja, fornecedor etc...
    const storeInfo = orderData?.filteredData?.[0]?.storeInfo?.store ?? {};
    const now = new Date();

    const upperLineInformation = {
        region: storeInfo.region ?? "Região",
        city: storeInfo.city ?? "Cidade",
        name: storeInfo.name ?? "Loja",
        cnpj: storeInfo.cnpj ?? "00.000.000/0000-00",
        address: storeInfo.address ?? "Endereço"
    };

    const bottomLineInformation = {
        supplierName: currentSupplierName,
        name: username ?? userInfo?.tenant?.name,
        orderDate: now.toLocaleDateString('pt-BR')
    };

    // colunas que formam a tabela e métodos de criação da ordem de pedido
    const columns = useMemo(() => [
        {
        header: COLUMNS_LABELS.PRODUCT,
        accessorKey: COLUMNS_IDENTIFIERS.PRODUCT_NAME,
        cell: info => (
            <div style={{ fontWeight: '500', color: '#374151' }}>
            {info.getValue()}
            </div>
        ),
        enableSorting: true
        },
        {
        header: COLUMNS_LABELS.EFFECTIVE_PURCHASE,
        accessorKey: COLUMNS_IDENTIFIERS.EFFECTIVE_PURCHASE,
        id: COLUMNS_IDENTIFIERS.EFFECTIVE_PURCHASE,
        cell: (props) => (
            <EffectivePurchaseCell
            {...props}
            showInputs={showInputs}
            inputs={inputs}
            setInputs={setInputs}
            saveLoading={saveLoading}/>
        ),
        enableSorting: true
        },
        {
        header: COLUMNS_LABELS.PACK_TO_UNIT_RATIO,
        accessorKey: COLUMNS_IDENTIFIERS.PACK_TO_UNIT_RATIO,
        id: COLUMNS_IDENTIFIERS.PACK_TO_UNIT_RATIO,
        cell: (props) => (
            <PackToUnitRatioCell
            {...props}
            showInputs={showInputs}
            inputs={inputs}
            setInputs={setInputs}
            saveLoading={saveLoading}/> 
        ),
        enableSorting: true
        },
        {
        header: COLUMNS_LABELS.UNIT_OF_MEASURE,
        accessorKey: COLUMNS_IDENTIFIERS.PREFIX_NAME,
        id: COLUMNS_IDENTIFIERS.PREFIX_NAME,
        cell: (props) => (
            <PrefixNameCell
            {...props}
            showInputs={showInputs}
            inputs={inputs}
            setInputs={setInputs}
            prefixes={prefixes}
            saveLoading={saveLoading}/>        
        ),
        enableSorting: true
        }
    ], [inputs, showInputs, saveLoading, prefixes]);    

    const handlePrevSupplier = () => setCurrentSupplierIndex(i => Math.max(i - 1, 0));
    const handleNextSupplier = () => setCurrentSupplierIndex(i => Math.min(i + 1, supplierIds.length - 1));

    const orderCreation = () => {
        const todayISO = new Date();
        const currentSupplierId = supplierIds[currentSupplierIndex];
        const storeId = storeInfo.storeId;

        const orderPayload = {
            storeId: storeId,
            supplierId: currentSupplierId,
            orderDate: todayISO,
            userId: userInfo?.userId,
            orderStatus: ORDER_STATUS.REALIZED
        };

        const orderItemsPayload = products.map(product => {
            const productId = product.productId;
            const manualData = inputs?.[productId];
            const raw = product;
            const packToUnitRatio = raw.packToUnitRatio;

            const preferedSourceMap = {
                [COLUMNS_IDENTIFIERS.PURCHASE_REC_7]: 7,
                [COLUMNS_IDENTIFIERS.PURCHASE_REC_14]: 14,
                [COLUMNS_IDENTIFIERS.PURCHASE_REC_21]: 21
            }

            return {
                orderDate: todayISO,
                userId: userInfo?.userId,
                storeId: storeId,
                supplierId: currentSupplierId,
                productId: productId,
                effectivePurchase: createPurchaseValueStrategy({ item: raw, conditionValue: true, isOrderHystoryPage: false }),
                prefixName: raw.prefixName ?? manualData?.prefixName,
                productName: raw.productName ?? manualData?.productName,
                prefixId: raw.prefixId ?? manualData?.prefixId,
                orderItemStatus: ORDER_ITEM_STATUS.ACTIVE,
                packToUnitRatio: packToUnitRatio,
                purchaseRecommendationId: raw.purchaseRecommendationId,
                purchaseDate: raw.purchaseDate,
                purchaseRecommHorizont: Number(preferedSourceMap[raw.preferedSource])
            };
        });

        createOrder(
            { ...orderPayload, items: orderItemsPayload },
            {
                onSuccess: (createdOrder) => {
                    setProcessedSupplierIds(prev => {
                        const updated = [...prev, currentSupplierId];
                        const remaining = supplierIds.filter(id => !updated.includes(id));
                        if (remaining.length === 0) {
                            onOrderComplete(createdOrder);

                        } else {
                            setCurrentSupplierIndex(supplierIds.indexOf(remaining[0]));
                        }
                        return updated;
                    });

                refetch();
                }
            }
        );
    };

    useEffect(() => {
        if (orderData?.selectedSupplierId) {
            const idx = supplierIds.indexOf(orderData.selectedSupplierId);
            if (idx >= 0 && idx !== currentSupplierIndex) {
                setCurrentSupplierIndex(idx);
            }
        }
    }, [orderData?.selectedSupplierId, supplierIds]);

    useEffect(() => setProducts(enrichedProducts), [enrichedProducts]);

    return {
        actions: {
            handlePrevSupplier,
            handleNextSupplier,
            orderCreation
        },
        ctx: {
            upperLineInformation,
            bottomLineInformation,
            currentSupplierIndex,
            supplierIds,
            processedSupplierIds,
            currentSupplierId,
            products,
            columns,
            currentSupplierName,
            createdOrder
        },
        uiState: {
            hasMultipleSuppliers,
            isCreatingOrder,
            isCreateOrderSuccess
        }
    };
};